// Multi-tenant platform code.
//
// Each operator has a unique registration code. Players who arrive through an
// operator link (/register?p=<CODE>, or any URL carrying ?p=<CODE>) must be
// bound to that operator's platform. We persist the code early (from the
// launch URL) so it survives navigation to /register and the guest games
// preview, then send it to the backend on register. This is DISTINCT from the
// referral system (?ref= / referralCode) — it is a separate, additional field.

const PLATFORM_CODE_KEY = 'platformCode';
const REFERRAL_CODE_KEY = 'referralCode';

// Capture ?p=<CODE> from the current URL and persist it. Called once at
// bootstrap, before React Router reads the URL. Safe to call with no param
// present — it only writes when a non-empty code is found, so an operator link
// captured earlier isn't clobbered by a later plain navigation.
export function capturePlatformCode(): void {
  try {
    const code = new URLSearchParams(window.location.search).get('p');
    if (code && code.trim()) {
      localStorage.setItem(PLATFORM_CODE_KEY, code.trim());
    }
  } catch {
    /* ignore — no storage / no URL access */
  }
}

export function getPlatformCode(): string | null {
  try {
    const code = localStorage.getItem(PLATFORM_CODE_KEY);
    return code && code.trim() ? code.trim() : null;
  } catch {
    return null;
  }
}

export function clearPlatformCode(): void {
  try {
    localStorage.removeItem(PLATFORM_CODE_KEY);
  } catch {
    /* ignore */
  }
}

// Persist a platform code the app learned indirectly (e.g. the referrer's
// tenant, resolved from a ?ref= link) so the guest games preview shows the
// right operator's catalog before signup. Same store as capturePlatformCode.
export function setPlatformCode(code?: string | null): void {
  try {
    if (code && code.trim()) localStorage.setItem(PLATFORM_CODE_KEY, code.trim());
  } catch {
    /* ignore */
  }
}

// Referral code capture — mirrors the platform-code flow so a ?ref=<CODE> link
// survives navigation to /register (and the guest preview) the same way ?p= does.
export function captureReferralCode(): void {
  try {
    const code = new URLSearchParams(window.location.search).get('ref');
    if (code && code.trim()) {
      localStorage.setItem(REFERRAL_CODE_KEY, code.trim());
    }
  } catch {
    /* ignore */
  }
}

export function getReferralCode(): string | null {
  try {
    const code = localStorage.getItem(REFERRAL_CODE_KEY);
    return code && code.trim() ? code.trim() : null;
  } catch {
    return null;
  }
}

export function clearReferralCode(): void {
  try {
    localStorage.removeItem(REFERRAL_CODE_KEY);
  } catch {
    /* ignore */
  }
}
